import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Armchair, CirclePause, Plus, ShoppingCart, Trash2, UserPlus, ChevronRight } from 'lucide-react';
import { ORDER_TYPES } from '@/utils/constants';
import { formatCurrency } from '@/utils/formatters';
import { PageShell } from '@/components/layout/PageShell';
import { Alert, Button, Card, IconButton, Input, SegmentedControl } from '@/components/ui';
import { useCartController } from './useCartController';
import { CartLine } from './CartLine';
import { CartTotals } from './CartTotals';
import { DiscountField } from './DiscountField';
import { ClearCartSheet, CustomerSheet, HoldCartSheet } from './CartSheets';
import { CustomerAutocomplete } from './CustomerAutocomplete';

/**
 * 05 Keranjang. Phone: T3 task flow with pinned "Bayar" footer. Landscape: items column + 400px customer panel.
 * All cart logic lives in useCartController; totals are the server's.
 */
const CartPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const cart = useCartController();
    const [sheet, setSheet] = useState<'hold' | 'clear' | 'customer' | null>(null);

    if (!cart.shopId) return null;

    const orderTypeOptions = (short: boolean) =>
        ORDER_TYPES.map((v) => ({ value: v, label: t(`cart.${short ? 'orderTypesShort' : 'orderTypes'}.${v}` as 'cart.orderTypes.DINE_IN') }));
    const payLabel = cart.calculation ? t('cart.payCta', { amount: formatCurrency(cart.calculation.grandTotal) }) : t('cart.proceedToCheckout');
    const hasItems = cart.items.length > 0;

    const payButton = (
        <Button variant="primary" size="lg" fullWidth onClick={cart.checkout} disabled={!cart.canCheckout} loading={cart.isCalculating && !cart.calculation}>
            {payLabel}
        </Button>
    );

    const discountField = <DiscountField cart={cart} />;

    return (
        <PageShell
            title={<>{t('cart.title')} {cart.itemCount > 0 && <span className="text-ink-muted">{cart.itemCount}</span>}</>}
            backTo="/catalog"
            padded={false}
            actions={hasItems && (
                <>
                    <div className="hidden w-[380px] lg:block">
                        <SegmentedControl fullWidth value={cart.orderType} onChange={cart.setOrderType} options={orderTypeOptions(false)} />
                    </div>
                    <button type="button" onClick={() => setSheet('hold')} className="flex h-10 items-center gap-1.5 rounded-pill bg-surface-card px-3.5 text-[13px] font-extrabold text-ink-brand shadow-sm lg:h-11 lg:px-4 lg:text-sm">
                        <CirclePause className="h-4 w-4" />
                        {t('cart.hold')}
                    </button>
                    <IconButton variant="danger" size="xs" label={t('cart.clearCart')} onClick={() => setSheet('clear')} className="lg:!h-11 lg:!w-11"><Trash2 /></IconButton>
                </>
            )}
            footer={hasItems && <div className="lg:hidden">{payButton}</div>}
        >
            {!hasItems ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 px-5 text-center">
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-surface-sunken text-primary-300"><ShoppingCart className="h-8 w-8" /></span>
                    <h3 className="text-lg font-black">{t('cart.empty')}</h3>
                    <p className="text-sm text-ink-muted">{t('cart.emptySubtitle')}</p>
                    <Button variant="primary" onClick={() => navigate('/catalog')}>{t('cart.browseCatalog')}</Button>
                </div>
            ) : (
                <div className="flex h-full min-h-0 flex-col lg:flex-row">
                    {/* Items column */}
                    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-5 pb-5 lg:gap-4 lg:px-7 lg:pb-7">
                        <div className="lg:hidden">
                            <SegmentedControl fullWidth value={cart.orderType} onChange={cart.setOrderType} options={orderTypeOptions(true)} />
                        </div>
                        {cart.error && <Alert variant="error" message={cart.error} />}
                        <Card padding="none" className="px-4 lg:px-5">
                            {cart.items.map((item, idx) => (
                                <CartLine
                                    key={item.compositeId}
                                    item={item}
                                    thumbSize={48}
                                    stepperSize="md"
                                    className={idx < cart.items.length - 1 ? 'border-b border-line-subtle' : ''}
                                    onQty={(q) => cart.setQty(item.compositeId, q)}
                                    onNotes={(n) => cart.setItemNotes(item.compositeId, n)}
                                    onRemove={() => cart.remove(item.compositeId)}
                                />
                            ))}
                        </Card>
                        <button type="button" onClick={() => navigate('/catalog')} className="hidden h-[52px] items-center justify-center gap-2 rounded-lg border border-dashed border-line text-sm font-extrabold text-ink-brand hover:bg-brandsoft lg:flex">
                            <Plus className="h-[17px] w-[17px]" />
                            {t('cart.addProduct')}
                        </button>

                        {/* Phone-only sections */}
                        <button type="button" onClick={() => setSheet('customer')} className="flex items-center gap-3 rounded-lg border border-dashed border-line bg-surface-card px-4 py-3.5 text-left lg:hidden">
                            <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full bg-brandsoft text-ink-brand"><UserPlus className="h-[18px] w-[18px]" /></span>
                            <span className="flex min-w-0 flex-1 flex-col">
                                <span className="text-sm font-extrabold">{cart.requireCustomerInfo ? t('cart.customerInfoRequired') : t('cart.customerData')}</span>
                                <span className="truncate text-xs text-ink-muted">
                                    {[cart.customerName, cart.customerPhone, cart.notes].filter(Boolean).join(' · ') || t('cart.customerHint')}
                                </span>
                            </span>
                            <ChevronRight className="h-[18px] w-[18px] text-ink-muted" />
                        </button>
                        <Card padding="sm" className="lg:hidden">{discountField}</Card>
                        <Card padding="sm" className="lg:hidden"><CartTotals calculation={cart.calculation} loading={cart.isCalculating} /></Card>
                    </div>

                    {/* Landscape customer panel */}
                    <aside className="hidden w-[400px] shrink-0 flex-col gap-3.5 border-l border-line-subtle bg-surface-card p-[22px] lg:flex">
                        <h2 className="text-[17px] font-black">
                            {cart.requireCustomerInfo ? t('cart.customerInfoRequired') : t('cart.customerData')}
                            {!cart.requireCustomerInfo && <span className="ml-1 text-[13px] font-semibold text-ink-muted">· {t('cart.optional').toLowerCase()}</span>}
                        </h2>
                        <div className="flex flex-col gap-2.5 [&_label]:hidden">
                            <CustomerAutocomplete shopId={cart.shopId} name={cart.customerName} phone={cart.customerPhone} required={cart.requireCustomerInfo} onChange={cart.setCustomer} icons />
                        </div>
                        <Input fieldSize="md" leftIcon={<Armchair />} placeholder={t('cart.addNotes')} value={cart.notes} onChange={(e) => cart.setNotes(e.target.value)} />
                        {discountField}
                        <div className="flex-1" />
                        <div className="rounded-lg bg-surface-sunken p-4">
                            <CartTotals calculation={cart.calculation} loading={cart.isCalculating} large />
                        </div>
                        {payButton}
                    </aside>
                </div>
            )}

            <HoldCartSheet cart={cart} isOpen={sheet === 'hold'} onClose={() => setSheet(null)} />
            <ClearCartSheet cart={cart} isOpen={sheet === 'clear'} onClose={() => setSheet(null)} />
            <CustomerSheet cart={cart} isOpen={sheet === 'customer'} onClose={() => setSheet(null)} />
        </PageShell>
    );
};

export default CartPage;
